import { Children, cloneElement, useEffect, useMemo, useState } from 'react';

import { useURLQuery } from '../../Common/Hook/urlQuery';
import { usePanelReportsGetQuery } from '../../Common/Queries/panelReportsQueries';

import { isValidDate, isValidID } from '../../utils';

import {
  INTERVIEWS_ENDPOINT,
  PANEL_TABLE_COLUMNS_ID,
  PREVIOUS_INTERVIEWS_ENDPOINT,
} from '../../constants';

const buildInterviewLink = ({ userId, view, page = 1, from, to, source = 'reports' }) => {
  const isPreviousInterview = view === 'previous-interviews';
  const params = new URLSearchParams({
    userID: userId,
    view,
    [isPreviousInterview ? 'past-interviews-page' : 'upcoming-interviews-page']: page,
    source,
  });

  if (from) params.set('startTimeRange.start-time', from);
  if (to) params.set('startTimeRange.end-time', to);

  return `${
    isPreviousInterview ? PREVIOUS_INTERVIEWS_ENDPOINT : INTERVIEWS_ENDPOINT
  }?${params.toString()}`;
};

const getPanelReportValue = (row, key) =>
  row[key] ?? row.interview_summary?.[key] ?? row.interview_status?.[key];

const normalizePanelReport = (row, filters = {}) => {
  const userId = row.user?.id;
  const upcomingLink =
    row.link?.upcomingLink ??
    row.upcomingLink ??
    buildInterviewLink({
      userId,
      view: 'upcoming-interviews',
      page: 1,
      from: filters.fromDate,
      to: filters.toDate,
    });
  const previousLink =
    row.link?.previousLink ??
    row.previousLink ??
    buildInterviewLink({
      userId,
      view: 'previous-interviews',
      page: 1,
      from: filters.fromDate,
      to: filters.toDate,
    });
  const normalizedColumns = PANEL_TABLE_COLUMNS_ID.reduce((acc, key) => {
    const value = getPanelReportValue(row, key);
    if (value !== undefined) acc[key] = value;
    return acc;
  }, {});

  return {
    ...row,
    ...normalizedColumns,
    upcomingLink,
    previousLink,
    link: {
      ...row.link,
      upcomingLink,
      previousLink,
    },
  };
};

const initialValues = {
  passed_interviews: {},
  failed_interviews: {},
  canceled_interviews: {},
  declined_interviews: {},
  pending_interviews: {},
  feedback_pending_interviews: {},
  result_pending_interviews: {},
  upcoming_interviews: {},
  previous_interviews: {},
  total_interviews: {},
  order: 'asc',
  orderBy: 'name',
  searchValue: '',
  page: 0,
  fromDate: '',
  toDate: '',
  selectedStatus: 'month',
  showRange: false,
};

export const PanelReportsListContainer = ({ children, filters = {}, updateFilters = () => {} }) => {
  const urlQuery = useURLQuery();

  const initialSearch = urlQuery.get('search');
  const initialFromDate = isValidDate(urlQuery.get('from')) && urlQuery.get('from');
  const initialToDate = isValidDate(urlQuery.get('to')) && urlQuery.get('to');
  const initialPage = isValidID(urlQuery.get('page')) && urlQuery.get('page');

  const [allData, setAllData] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [page, setPage] = useState(1);

  const initialFilterValuesToApply = {
    ...initialValues,
    searchValue: initialSearch || '',
    page: initialPage ? Number(initialPage - 1) : 0,
    ...(initialFromDate &&
      initialToDate && {
        fromDate: initialFromDate,
        toDate: initialToDate,
        selectedStatus: 'dateRange',
        showRange: true,
      }),
    ...(initialFromDate &&
      initialToDate &&
      initialFromDate === initialToDate && {
        fromDate: initialFromDate,
        toDate: initialToDate,
        selectedStatus: 'select',
        showRange: false,
      }),
  };

  useEffect(() => {
    updateFilters(initialFilterValuesToApply);
  }, []);

  const filterObj = (obj) =>
    Object.keys(obj).reduce((acc, key) => {
      if (
        PANEL_TABLE_COLUMNS_ID.includes(key) &&
        ((obj[key]?.min && obj[key]?.min !== '') || (obj[key]?.max && obj[key]?.max !== ''))
      ) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});

  const getQueryParameters = {
    ...filterObj(filters),
    'dateRange.fromDate': filters.fromDate,
    'dateRange.toDate': filters.toDate,
    'dateRange.includeDate': true,
    sort: (filters.order || 'asc').toUpperCase(),
    orderBy: filters.orderBy,
    q: filters.searchValue?.length >= 2 ? filters.searchValue : '',
  };

  const panelReports = usePanelReportsGetQuery({
    ...getQueryParameters,
    page: Number((filters.page || 0) + 1),
    limit: 20,
  });

  const panelReportsAllData = usePanelReportsGetQuery(
    { ...getQueryParameters, limit: 100, page },
    { enabled }
  );

  const normalizedPanelReports = useMemo(() => {
    if (!panelReports.data?.data) return panelReports;

    const normalizedData = panelReports.data.data.map((row) => normalizePanelReport(row, filters));

    return {
      ...panelReports,
      data: {
        ...panelReports.data,
        data: normalizedData,
      },
    };
  }, [panelReports.data, filters.fromDate, filters.toDate]);

  useEffect(() => {
    if (panelReportsAllData.isSuccess && enabled) {
      const transformedData =
        panelReportsAllData.data?.data?.map((row) => {
          const { user, ...rest } = normalizePanelReport(row, filters);

          return {
            userId: user.id,
            name: user.name,
            ...rest,
          };
        }) || [];

      setAllData((prev) => [...prev, ...transformedData]);
    }

    if (panelReportsAllData.data?.meta?.total > page * 100) {
      setPage((prev) => prev + 1);
    }
  }, [panelReportsAllData.isSuccess]);

  useEffect(() => {
    if (allData.length > 0) {
      setAllData([]);
      setPage(1);
    }
  }, [filters]);

  const handleOnFilterChange = (updatedFilters) => {
    updateFilters((prev) => ({ ...prev, ...updatedFilters }));
  };

  const child = Children.only(children);

  return cloneElement(child, {
    panelReports: normalizedPanelReports,
    filters,
    filterHandler: handleOnFilterChange,
    enabled,
    setEnabled,
    allData,
    panelReportsAllData,
  });
};
